import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useUserStore } from './useUser';
import { useTCG } from './useTCG';
import { addCardToCollection, removeCardFromCollection, getUserCollection, getCollectionValue, bulkAddCardsToCollection, clearUserCollection } from '../utilities/firebase';
import { logEvent } from '../utilities/ga';

// Create a Zustand store for collection state
export const useCollectionStore = create(
  persist(
    (set) => ({
      // This will be deprecated in favor of useTCG, but kept for backward compatibility
      activeCategory: 'mtg',
      setActiveCategory: (category) => set({ activeCategory: category }),
    }),
    {
      name: 'TCGStacked.Collection',
    }
  )
);

// Hook to interact with the collection
export const useCollection = () => {
  const { user } = useUserStore();
  const { activeCategory: legacyCategory, setActiveCategory: setLegacyCategory } = useCollectionStore();
  const { activeTCG, setActiveTCG } = useTCG();
  const queryClient = useQueryClient();

  // Use the TCG context as the source of truth, but fall back to the legacy category
  const activeCategory = activeTCG || legacyCategory;

  // Update both the legacy category and the TCG context
  const setActiveCategory = (category) => {
    // Only update if the category is different from the current one
    if (category !== activeCategory) {
      setLegacyCategory(category);
      setActiveTCG(category);

      // Invalidate queries for the new category to force a refresh
      if (user?.uid) {
        queryClient.invalidateQueries(['userCollection', user.uid, category]);
        queryClient.invalidateQueries(['collectionValue', user.uid, category]);
      }
    }
  };

  // Query to fetch the user's collection
  const {
    data: collection = [],
    isLoading: isLoadingCollection,
    error: collectionError,
    refetch: refetchCollection,
  } = useQuery({
    queryKey: ['userCollection', user?.uid, activeCategory],
    queryFn: () => getUserCollection(user?.uid, activeCategory),
    enabled: !!user,
  });

  // Query to fetch the total value of the collection
  const {
    data: collectionValue = '0.00',
    isLoading: isLoadingValue,
    error: valueError,
    refetch: refetchValue,
  } = useQuery({
    queryKey: ['collectionValue', user?.uid, activeCategory],
    queryFn: () => getCollectionValue(user?.uid, activeCategory),
    enabled: !!user,
  });

  // Mutation to add a card to the collection
  const addToCollection = useMutation({
    mutationFn: async ({ card, quantity = 1, category = activeCategory, condition = 'NM', language = 'English', finish = 'Non-Foil', purchasePrice, isProxy = false }) => {
      const result = await addCardToCollection(user?.uid, card, quantity, category, condition, language, finish, purchasePrice, isProxy);

      // Check if the result indicates success or failure
      if (!result.success) {
        console.error('useCollection: Failed to add card', result.error);
        throw new Error(result.error || 'Failed to add card to collection');
      }

      return result;
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries(['userCollection', user?.uid, activeCategory]);
      queryClient.invalidateQueries(['collectionValue', user?.uid, activeCategory]);
      toast.success('Card added to collection');
      logEvent({
        action: 'addToCollection',
        params: {
          category: activeCategory,
        },
      });
    },
    onError: (error) => {
      console.error('useCollection: Error adding card to collection:', error);
      toast.error(`Failed to add card to collection: ${error.message || 'Unknown error'}`);
    },
  });

  // Mutation to remove a card from the collection
  const removeFromCollection = useMutation({
    mutationFn: ({ cardId, quantity = 1, category = activeCategory }) => removeCardFromCollection(user?.uid, cardId, quantity, category),
    onSuccess: (result) => {
      if (result.success) {
        queryClient.invalidateQueries(['userCollection', user?.uid, activeCategory]);
        queryClient.invalidateQueries(['collectionValue', user?.uid, activeCategory]);
        toast.success('Card removed from collection');
        logEvent({
          action: 'removeFromCollection',
          params: {
            category: activeCategory,
            action: result.action,
          },
        });
      } else {
        console.error('Error removing card from collection:', result.error);
        toast.error(result.error || 'Failed to remove card from collection');
      }
    },
    onError: (error) => {
      console.error('Error removing card from collection:', error);
      toast.error('Failed to remove card from collection');
    },
  });

  // Mutation to bulk add cards to the collection
  const bulkAddToCollection = useMutation({
    mutationFn: ({ cards, category = activeCategory }) => bulkAddCardsToCollection(user?.uid, cards, category),
    onSuccess: (result) => {
      if (result.success) {
        queryClient.invalidateQueries(['userCollection', user?.uid, activeCategory]);
        queryClient.invalidateQueries(['collectionValue', user?.uid, activeCategory]);
        toast.success('Cards added to collection');
        logEvent({
          action: 'bulkAddToCollection',
          params: {
            category: activeCategory,
            cardsProcessed: result.processedCards?.length || 0,
          },
        });
      } else {
        console.error('Error bulk adding cards to collection:', result.error);
        toast.error(result.error || 'Failed to add cards to collection');
      }
    },
    onError: (error) => {
      console.error('Error bulk adding cards to collection:', error);
      toast.error('Failed to add cards to collection');
    },
  });

  // Check if a card is in the collection
  const isInCollection = (cardId) => {
    return collection.some((card) => card.id === cardId || card.cardId === cardId);
  };

  // Get the quantity of a card in the collection
  const getQuantityInCollection = (cardId) => {
    const card = collection.find((card) => card.id === cardId || card.cardId === cardId);
    return card ? card.quantity : 0;
  };

  // Calculate collection statistics
  const getCollectionStats = () => {
    if (!collection.length)
      return {
        totalCards: 0,
        uniqueCards: 0,
        rarityDistribution: {},
        setDistribution: {},
      };

    const uniqueCards = collection.length;
    const totalCards = collection.reduce((total, card) => total + (card.quantity || 0), 0);

    // Calculate rarity distribution
    const rarityDistribution = collection.reduce((acc, card) => {
      const rarity = card.rarity || 'unknown';
      if (!acc[rarity]) acc[rarity] = 0;
      acc[rarity] += card.quantity || 0;
      return acc;
    }, {});

    // Calculate set distribution
    const setDistribution = collection.reduce((acc, card) => {
      const set = card.setName || 'unknown';
      if (!acc[set]) acc[set] = 0;
      acc[set] += card.quantity || 0;
      return acc;
    }, {});

    return {
      totalCards,
      uniqueCards,
      rarityDistribution,
      setDistribution,
    };
  };

  // Mutation to clear the entire collection
  const clearCollection = useMutation({
    mutationFn: ({ category = activeCategory }) => clearUserCollection(user?.uid, category),
    onSuccess: () => {
      queryClient.invalidateQueries(['userCollection', user?.uid, activeCategory]);
      queryClient.invalidateQueries(['collectionValue', user?.uid, activeCategory]);
      toast.success('Collection cleared successfully');
      logEvent({
        action: 'clearCollection',
        params: {
          category: activeCategory,
        },
      });
    },
    onError: (error) => {
      console.error('Error clearing collection:', error);
      toast.error('Failed to clear collection');
    },
  });

  return {
    collection,
    isLoadingCollection,
    collectionError,
    refetchCollection,
    collectionValue,
    isLoadingValue,
    valueError,
    refetchValue,
    addToCollection,
    removeFromCollection,
    bulkAddToCollection,
    clearCollection,
    isInCollection,
    getQuantityInCollection,
    getCollectionStats,
    activeCategory,
    setActiveCategory,
    user,
  };
};
